import { useMemo } from 'react';

import { useTableContext } from '#/hooks/useTableContext';
import { toast } from '#/utils/toast';

import { SelectCore } from '#/modules/commonComponents/form/select';

const MAX_PAGE_SIZE = 200;

const DEFAULT_PAGE_SIZE_OPTIONS = [
  { value: 10, label: '10' },
  { value: 20, label: '20' },
  { value: 50, label: '50' },
  { value: 100, label: '100' },
  { value: 200, label: '200' },
];

/** 僅覆寫 control／尺寸；menu／menuPortal z-index 由 SelectCore 合併層保留。 */
const COMPACT_SELECT_STYLES = {
  control: (base, state) => ({
    ...base,
    minHeight: '2rem',
    minWidth: '4.5rem',
    borderColor: state.isFocused ? 'var(--color-primary-500)' : 'var(--color-gray-300)',
    boxShadow: state.isFocused ? '0 0 0 1px rgba(14, 165, 233, 0.35)' : 'none',
    cursor: 'pointer',
    '&:hover': {
      borderColor: 'var(--color-primary-500)',
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 8px',
  }),
  singleValue: (base) => ({
    ...base,
    margin: 0,
    color: 'var(--color-gray-700)',
    textAlign: 'center',
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    color: 'var(--color-gray-700)',
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: '2rem',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: '0 6px',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  menu: (base) => ({
    ...base,
    minWidth: '5rem',
  }),
};

function parsePositiveInteger(raw) {
  const trimmed = String(raw ?? '').trim();
  if (!/^\d+$/.test(trimmed)) {
    return null;
  }

  const value = Number(trimmed);
  if (!Number.isInteger(value) || value <= 0) {
    return null;
  }

  return value;
}

function isValidNewPageSizeOption(inputValue, options) {
  const parsed = parsePositiveInteger(inputValue);
  if (parsed == null) {
    return false;
  }

  return !options.some((option) => option.value === parsed);
}

function buildPageSizeOptions(pageSize) {
  if (!pageSize || DEFAULT_PAGE_SIZE_OPTIONS.some((option) => option.value === pageSize)) {
    return DEFAULT_PAGE_SIZE_OPTIONS;
  }

  return [
    ...DEFAULT_PAGE_SIZE_OPTIONS,
    { value: pageSize, label: String(pageSize) },
  ].sort((a, b) => a.value - b.value);
}

const TablePageSizeSelect = () => {
  const { pageSize, handlePageSizeChange } = useTableContext();
  const options = useMemo(() => buildPageSizeOptions(pageSize), [pageSize]);

  function commitPageSize(rawValue) {
    const nextSize = typeof rawValue === 'number'
      ? rawValue
      : parsePositiveInteger(rawValue);

    if (nextSize == null) {
      toast.warning('請輸入正整數');
      return;
    }

    if (nextSize > MAX_PAGE_SIZE) {
      toast.warning('最多 200 筆');
      return;
    }

    if (nextSize === pageSize) {
      return;
    }

    handlePageSizeChange(nextSize);
  }

  return (
    <div className="flex items-center gap-1.5">
      <div className="w-24">
        <SelectCore
          id="table-page-size"
          creatable
          options={options}
          value={pageSize}
          onChange={commitPageSize}
          placeholder="10"
          isValidNewOption={(inputValue) => isValidNewPageSizeOption(inputValue, options)}
          formatCreateLabel={(inputValue) => `使用 ${inputValue} 筆`}
          styles={COMPACT_SELECT_STYLES}
          classNames={{
            singleValue: () => 'text-sm text-gray-700',
            input: () => 'text-sm text-gray-700',
            placeholder: () => 'text-sm text-gray-400',
          }}
        />
      </div>
      <span className="shrink-0 text-sm text-gray-500">筆</span>
    </div>
  );
};

export default TablePageSizeSelect;
